const DEFAULT_MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;

// #154 follow-up: a real phone-camera photo (e.g. 4000x2256) sent to the public OCSR service
// as-is measurably increases the chance of a backend timeout/502 on complex structures --
// confirmed by manual testing (simple/small images succeed reliably; the same complex structure
// as a full-resolution photo failed, while staying well under the server's own upload size
// limit). Downscaling client-side, before the upload even starts, reduces both the payload size
// and (plausibly) the upstream processing time, without needing any server-side change.
export async function downscaleImageIfNeeded(file: File, maxDimension: number = DEFAULT_MAX_DIMENSION): Promise<File> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // Not a decodable image (or the browser lacks createImageBitmap for this input) -- let the
    // server's own validation reject it rather than failing the whole import client-side.
    return file;
  }
  try {
    if (bitmap.width <= maxDimension && bitmap.height <= maxDimension) return file;

    const scale = maxDimension / Math.max(bitmap.width, bitmap.height);
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) return file;

    const downscaledName = file.name.replace(/\.\w+$/, '') + '.jpg';
    return new File([blob], downscaledName, { type: 'image/jpeg' });
  } finally {
    bitmap.close();
  }
}
